import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Banknote,
  BarChart3,
  BedDouble,
  BookOpen,
  Boxes,
  Building2,
  CalendarClock,
  Clock,
  Carrot,
  ChefHat,
  ChevronDown,
  ClipboardList,
  CookingPot,
  DoorOpen,
  FileInput,
  FileText,
  Gift,
  HeartPulse,
  LayoutDashboard,
  LayoutGrid,
  Library,
  LogOut,
  Menu as MenuIcon,
  MonitorSmartphone,
  Moon,
  PackagePlus,
  Receipt,
  Settings,
  ShoppingCart,
  SlidersHorizontal,
  Stethoscope,
  Sun,
  Ticket,
  Trash2,
  TrendingUp,
  Truck,
  UserCheck,
  UserCog,
  UserRound,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';
import i18n from './i18n.js';
import { isSupabaseConfigured, supabase } from './supabase.js';
import { useAuth, type Role } from './useAuth.js';
import { SignIn } from './views/SignIn.js';
import { LandingView } from './views/LandingView.js';
import { ResetPassword } from './views/ResetPassword.js';
import { MenuManager } from './views/MenuManager.js';
import { ClientsManager } from './views/ClientsManager.js';
import { CashiersManager } from './views/CashiersManager.js';
import { IngredientsManager } from './views/IngredientsManager.js';
import { RecipesManager } from './views/RecipesManager.js';
import { OrdersView } from './views/OrdersView.js';
import { ShiftsView } from './views/ShiftsView.js';
import { ReceiptTemplateManager } from './views/ReceiptTemplateManager.js';
import { CustomerDisplayManager } from './views/CustomerDisplayManager.js';
import { DashboardView } from './views/DashboardView.js';
import { SalesView } from './views/SalesView.js';
import { MenuInsightsView } from './views/MenuInsightsView.js';
import { StaffInsightsView } from './views/StaffInsightsView.js';
import { InventoryInsightsView } from './views/InventoryInsightsView.js';
import { UsersView } from './views/UsersView.js';
import { InvoicesView } from './views/InvoicesView.js';
import { SettingsView } from './views/SettingsView.js';
import { StockInView } from './views/StockInView.js';
import { WasteLogView } from './views/WasteLogView.js';
import { PrepBatchView } from './views/PrepBatchView.js';
import { StockCountView } from './views/StockCountView.js';
import { SuppliersManager } from './views/SuppliersManager.js';
import { StaffView } from './views/StaffView.js';
import { TimesheetView } from './views/TimesheetView.js';
import { FloorPlanManager } from './views/FloorPlanManager.js';
import { BookingsView } from './views/BookingsView.js';
import { BookingsInsightsView } from './views/BookingsInsightsView.js';
import { RoomsManager } from './views/RoomsManager.js';
import { StaffCompsView } from './views/StaffCompsView.js';
import { ModifiersManager } from './views/ModifiersManager.js';
import { PosDiagnosticsView } from './views/PosDiagnosticsView.js';
import { BusinessHealthView } from './views/BusinessHealthView.js';
import { BooksView } from './views/BooksView.js';
import { PurchasesView } from './views/PurchasesView.js';
import { CashDropsView } from './views/CashDropsView.js';
import { LoyaltyManager } from './views/LoyaltyManager.js';

/** Read the current URL hash and return a valid View name, or null. */
function getViewFromHash(validViews: readonly View[]): View | null {
  const hash = window.location.hash.replace(/^#\//, '').split('?')[0];
  if (hash === 'comp-recipients') return 'staff';
  return validViews.includes(hash as View) ? (hash as View) : null;
}

type View =
  | 'dashboard'
  | 'sales'
  | 'menu-insights'
  | 'staff-insights'
  | 'staff-comps'
  | 'inventory-insights'
  | 'purchases'
  | 'clients'
  | 'menu'
  | 'modifiers'
  | 'users'
  | 'cashiers'
  | 'ingredients'
  | 'recipes'
  | 'orders'
  | 'shifts'
  | 'timesheet'
  | 'receipt'
  | 'customer-display'
  | 'invoices'
  | 'stock-in'
  | 'stock-count'
  | 'waste'
  | 'prep-batches'
  | 'suppliers'
  | 'staff'
  | 'floor-plan'
  | 'bookings'
  | 'booking-insights'
  | 'rooms'
  | 'pos-diagnostics'
  | 'business-health'
  | 'books'
  | 'cash-drops'
  | 'loyalty'
  | 'settings';

interface NavItem {
  view: View;
  i18nKey: string;
  roles: Role[];
  group: 'insights' | 'books' | 'operations' | 'management' | 'crm' | 'system' | 'admin';
}

const NAV_ICONS: Record<View, LucideIcon> = {
  dashboard: LayoutDashboard,
  sales: TrendingUp,
  'menu-insights': UtensilsCrossed,
  'staff-insights': Users,
  'staff-comps': Gift,
  'inventory-insights': Boxes,
  purchases: ShoppingCart,
  clients: Building2,
  orders: Receipt,
  shifts: CalendarClock,
  timesheet: Clock,
  'stock-count': ClipboardList,
  menu: BookOpen,
  modifiers: SlidersHorizontal,
  ingredients: Carrot,
  recipes: ChefHat,
  users: UserCog,
  cashiers: UserRound,
  receipt: FileText,
  'customer-display': MonitorSmartphone,
  invoices: FileInput,
  'stock-in': PackagePlus,
  waste: Trash2,
  'prep-batches': CookingPot,
  suppliers: Truck,
  staff: UserCheck,
  'floor-plan': LayoutGrid,
  bookings: BedDouble,
  'booking-insights': BarChart3,
  rooms: DoorOpen,
  'pos-diagnostics': Stethoscope,
  'business-health': HeartPulse,
  books: Library,
  'cash-drops': Banknote,
  loyalty: Ticket,
  settings: Settings,
};

const NAV: NavItem[] = [
  // — Insights —
  { view: 'dashboard', i18nKey: 'nav.dashboard', roles: ['owner', 'admin'], group: 'insights' },
  { view: 'sales', i18nKey: 'nav.sales', roles: ['owner', 'admin'], group: 'insights' },
  {
    view: 'menu-insights',
    i18nKey: 'nav.menuInsights',
    roles: ['owner', 'admin', 'manager'],
    group: 'insights',
  },
  {
    view: 'business-health',
    i18nKey: 'nav.businessHealth',
    roles: ['owner', 'admin'],
    group: 'insights',
  },
  {
    view: 'books',
    i18nKey: 'nav.books',
    roles: ['owner', 'admin', 'accountant'],
    group: 'books',
  },
  {
    view: 'staff-insights',
    i18nKey: 'nav.staffInsights',
    roles: ['owner', 'admin'],
    group: 'insights',
  },
  {
    view: 'staff-comps',
    i18nKey: 'nav.staffComps',
    roles: ['owner', 'admin'], // not manager — staff meal comp analytics
    group: 'insights',
  },
  {
    view: 'inventory-insights',
    i18nKey: 'nav.inventoryInsights',
    roles: ['owner', 'admin', 'manager'],
    group: 'insights',
  },
  {
    view: 'purchases',
    i18nKey: 'nav.purchases',
    roles: ['owner', 'admin', 'manager'],
    group: 'insights',
  },
  {
    view: 'booking-insights',
    i18nKey: 'nav.bookingInsights',
    roles: ['owner', 'admin', 'manager'],
    group: 'insights',
  },
  // — Operations —
  {
    view: 'orders',
    i18nKey: 'nav.orders',
    roles: ['owner', 'admin', 'manager'],
    group: 'operations',
  },
  {
    view: 'shifts',
    i18nKey: 'nav.shifts',
    roles: ['owner', 'admin', 'manager'],
    group: 'operations',
  },
  {
    view: 'timesheet',
    i18nKey: 'nav.timesheet',
    roles: ['owner', 'admin', 'manager'],
    group: 'operations',
  },
  {
    view: 'stock-count',
    i18nKey: 'nav.stockCount',
    roles: ['owner', 'admin', 'manager'],
    group: 'operations',
  },
  {
    view: 'cash-drops',
    i18nKey: 'nav.cashDrops',
    roles: ['owner', 'admin'],
    group: 'operations',
  },
  {
    view: 'bookings',
    i18nKey: 'nav.bookings',
    roles: ['owner', 'admin', 'manager'],
    group: 'operations',
  },
  // — Management —
  { view: 'menu', i18nKey: 'nav.menu', roles: ['owner', 'admin', 'manager'], group: 'management' },
  {
    view: 'modifiers',
    i18nKey: 'nav.modifiers',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'ingredients',
    i18nKey: 'nav.ingredients',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'recipes',
    i18nKey: 'nav.recipes',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'invoices',
    i18nKey: 'nav.invoices',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'stock-in',
    i18nKey: 'nav.stockIn',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'waste',
    i18nKey: 'nav.waste',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'prep-batches',
    i18nKey: 'nav.prepBatches',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'suppliers',
    i18nKey: 'nav.suppliers',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'staff',
    i18nKey: 'nav.staff',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'floor-plan',
    i18nKey: 'nav.floorPlan',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  {
    view: 'rooms',
    i18nKey: 'nav.rooms',
    roles: ['owner', 'admin', 'manager'],
    group: 'management',
  },
  // — CRM —
  {
    view: 'customer-display',
    i18nKey: 'nav.customerDisplay',
    roles: ['owner', 'admin'],
    group: 'crm',
  },
  {
    view: 'loyalty',
    i18nKey: 'nav.loyalty',
    roles: ['owner', 'admin'],
    group: 'crm',
  },
  // — System —
  { view: 'settings', i18nKey: 'nav.settings', roles: ['owner'], group: 'system' },
  {
    view: 'pos-diagnostics',
    i18nKey: 'nav.posDiagnostics',
    roles: ['owner', 'admin', 'manager'],
    group: 'system',
  },
  {
    view: 'receipt',
    i18nKey: 'nav.receipt',
    roles: ['owner', 'admin', 'manager'],
    group: 'system',
  },
  { view: 'users', i18nKey: 'nav.users', roles: ['owner'], group: 'system' },
  { view: 'cashiers', i18nKey: 'nav.cashiers', roles: ['owner'], group: 'system' },
  // — Admin —
  { view: 'clients', i18nKey: 'nav.clients', roles: ['super_admin'], group: 'admin' },
];

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'hy', label: 'ՀՅ' },
  { code: 'ru', label: 'RU' },
];

type Theme = 'dark' | 'light';

function applyTheme(t: Theme) {
  document.documentElement.setAttribute('data-theme', t === 'dark' ? 'dark' : '');
  localStorage.setItem('theme', t);
}

function getStoredTheme(): Theme {
  return (localStorage.getItem('theme') as Theme | null) ?? 'light';
}

const ALL_VIEWS: View[] = [
  'dashboard',
  'sales',
  'menu-insights',
  'staff-insights',
  'staff-comps',
  'inventory-insights',
  'purchases',
  'clients',
  'menu',
  'modifiers',
  'users',
  'cashiers',
  'ingredients',
  'recipes',
  'orders',
  'shifts',
  'timesheet',
  'stock-count',
  'receipt',
  'customer-display',
  'invoices',
  'stock-in',
  'waste',
  'prep-batches',
  'suppliers',
  'staff',
  'floor-plan',
  'bookings',
  'booking-insights',
  'rooms',
  'pos-diagnostics',
  'business-health',
  'books',
  'cash-drops',
  'loyalty',
  'settings',
];

export function App() {
  const { t } = useTranslation();
  const { session, loading, role, clientId, needsPasswordReset, clearPasswordRecovery } = useAuth();
  const [loyaltyProvisioned, setLoyaltyProvisioned] = useState(false);
  const [tableServiceEnabled, setTableServiceEnabled] = useState(false);
  const [bookingsEnabled, setBookingsEnabled] = useState(false);
  const navFiltered = NAV.filter((n) => {
    if (!n.roles.includes(role)) return false;
    if (n.view === 'loyalty' && role !== 'super_admin' && !loyaltyProvisioned) return false;
    if (n.view === 'floor-plan' && !tableServiceEnabled) return false;
    if ((n.view === 'bookings' || n.view === 'rooms' || n.view === 'booking-insights') && !bookingsEnabled)
      return false;
    return true;
  });
  const visibleNav = navFiltered;
  const [view, setView] = useState<View | null>(() => getViewFromHash(ALL_VIEWS));
  const [focusShiftId, setFocusShiftId] = useState<string | null>(null);
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  const [lang, setLang] = useState(i18n.language ?? 'en');
  const [clientName, setClientName] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem('nav_collapsed') ?? '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  function toggleGroup(g: NavItem['group']) {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [g]: !prev[g] };
      localStorage.setItem('nav_collapsed', JSON.stringify(next));
      return next;
    });
  }

  useEffect(() => {
    if (!supabase || !clientId || role === 'super_admin') {
      setLoyaltyProvisioned(false);
      return;
    }
    supabase
      .from('loyalty_settings')
      .select('platform_enabled')
      .eq('client_id', clientId)
      .maybeSingle()
      .then(({ data }) => setLoyaltyProvisioned(!!data?.platform_enabled));
  }, [clientId, role]);

  useEffect(() => {
    if (!supabase || !clientId) return;
    supabase
      .from('clients')
      .select('name, table_service_enabled, bookings_enabled')
      .eq('id', clientId)
      .single()
      .then(({ data }) => {
        if (data) {
          setClientName(data.name);
          setTableServiceEnabled(!!data.table_service_enabled);
          setBookingsEnabled(!!data.bookings_enabled);
        }
      });
  }, [clientId]);

  // Apply theme on init and change. Keep public pages light.
  useEffect(() => {
    if (!session) {
      document.documentElement.setAttribute('data-theme', '');
      return;
    }
    applyTheme(theme);
  }, [theme, session]);

  useEffect(() => {
    if (!session) return;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/login') {
      window.history.replaceState(null, '', `/${window.location.hash}`);
    }
  }, [session]);

  // Sync view state with browser back/forward
  useEffect(() => {
    const handler = () => setView(getViewFromHash(ALL_VIEWS));
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const effectiveView: View | null =
    view && visibleNav.some((n) => n.view === view) ? view : (visibleNav[0]?.view ?? null);

  const toggleTheme = () => setTheme((t: 'dark' | 'light') => (t === 'dark' ? 'light' : 'dark'));

  const changeLang = (code: string) => {
    void i18n.changeLanguage(code);
    setLang(code);
    localStorage.setItem('dashboard_lang', code);
  };

  function navigate(v: View) {
    window.history.pushState(null, '', `#/${v}`);
    setView(v);
    setMobileNavOpen(false);
  }

  function navigateToRecipe(recipeId: string) {
    window.history.pushState(null, '', `#/recipes?recipe=${encodeURIComponent(recipeId)}`);
    setView('recipes');
    setMobileNavOpen(false);
  }

  /** Jump to the Shifts view and focus a specific shift (from the Orders list). */
  function navigateToShift(shiftId: string) {
    setFocusShiftId(shiftId);
    navigate('shifts');
  }

  /** Jump to the Orders view filtered by a specific shift. */
  function navigateToOrders(shiftId: string) {
    window.history.pushState(null, '', `#/orders?shift=${encodeURIComponent(shiftId)}`);
    setView('orders');
    setMobileNavOpen(false);
  }

  if (!isSupabaseConfigured) return <NotConfigured />;
  if (loading) return <div className="auth-screen">{t('common.loading')}</div>;
  if (session && needsPasswordReset) {
    return <ResetPassword onDone={clearPasswordRecovery} />;
  }
  if (!session) {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/login') return <SignIn />;
    return <LandingView />;
  }

  if (role === 'anon') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="brand">Orio</div>
          <h2>{t('auth.noRoleTitle')}</h2>
          <p className="muted">{t('auth.noRoleBodyExtended')}</p>
          <button className="link" onClick={() => void supabase?.auth.signOut()}>
            {t('common.signOut')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      {mobileNavOpen && <div className="mobile-overlay" onClick={() => setMobileNavOpen(false)} />}
      <aside className={mobileNavOpen ? 'sidebar mobile-open' : 'sidebar'}>
        <div className="brand">
          <span>Orio</span>
          {clientName && <span className="brand-client">{clientName}</span>}
        </div>
        <nav>
          {(() => {
            const groups: NavItem['group'][] = [];
            for (const n of visibleNav) if (!groups.includes(n.group)) groups.push(n.group);
            return groups.map((g) => {
              const items = visibleNav.filter((n) => n.group === g);
              const activeInGroup = items.some((n) => n.view === effectiveView);
              // Never hide the group that holds the current page.
              const collapsed = !!collapsedGroups[g] && !activeInGroup;
              return (
                <div key={g} className={collapsed ? 'nav-group nav-group--collapsed' : 'nav-group'}>
                  <button
                    type="button"
                    className="nav-group-label"
                    onClick={() => toggleGroup(g)}
                    aria-expanded={!collapsed}
                  >
                    <span>{t(`nav.groups.${g}`)}</span>
                    <ChevronDown className="nav-group-chevron" />
                  </button>
                  {!collapsed &&
                    items.map((n) => {
                      const Icon = NAV_ICONS[n.view];
                      return (
                        <button
                          key={n.view}
                          type="button"
                          className={effectiveView === n.view ? 'active' : ''}
                          onClick={() => navigate(n.view)}
                        >
                          <Icon className="nav-icon" />
                          {t(n.i18nKey)}
                        </button>
                      );
                    })}
                </div>
              );
            });
          })()}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-account">
            <span className="sidebar-avatar" aria-hidden="true">
              {(session.user.email ?? '?').charAt(0).toUpperCase()}
            </span>
            <span className="sidebar-account-text">
              <span className="user-email" title={session.user.email ?? ''}>
                {session.user.email}
              </span>
              <span className="user-role">{t(`nav.role.${role}`, role.replace('_', ' '))}</span>
            </span>
            <button
              type="button"
              className="sidebar-btn-icon"
              onClick={() => void supabase?.auth.signOut()}
              title={t('common.signOut')}
              aria-label={t('common.signOut')}
            >
              <LogOut />
            </button>
          </div>
          <div className="sidebar-controls">
            <select
              value={lang}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => changeLang(e.target.value)}
              title={t('nav.language')}
              aria-label={t('nav.language')}
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="sidebar-btn-icon"
              onClick={toggleTheme}
              title={theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')}
              aria-label={theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')}
            >
              {theme === 'dark' ? <Sun /> : <Moon />}
            </button>
          </div>
        </div>
      </aside>

      <main className="content">
        {import.meta.env.VITE_DEMO_MODE === 'true' && (
          <div className="demo-banner">{t('demo.banner')}</div>
        )}
        <div className="mobile-topbar">
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => setMobileNavOpen(true)}
            aria-label={t('nav.openMenu')}
          >
            <MenuIcon />
          </button>
          <span className="mobile-topbar-title">Orio</span>
        </div>
        {effectiveView === 'dashboard' && (
          <DashboardView
            clientId={clientId}
            bookingsEnabled={bookingsEnabled}
            onNavigate={navigate as (view: string) => void}
          />
        )}
        {effectiveView === 'sales' && <SalesView clientId={clientId} />}
        {effectiveView === 'menu-insights' && <MenuInsightsView clientId={clientId} />}
        {effectiveView === 'staff-insights' && <StaffInsightsView clientId={clientId} />}
        {effectiveView === 'staff-comps' &&
          (role === 'owner' || role === 'admin') && (
            <StaffCompsView clientId={clientId} />
          )}
        {effectiveView === 'inventory-insights' && <InventoryInsightsView clientId={clientId} />}
        {effectiveView === 'purchases' && <PurchasesView clientId={clientId} />}
        {effectiveView === 'business-health' && <BusinessHealthView clientId={clientId} />}
        {effectiveView === 'books' && <BooksView clientId={clientId} />}
        {effectiveView === 'clients' && <ClientsManager />}
        {effectiveView === 'menu' && (
          <MenuManager clientId={clientId} onNavigateToRecipe={navigateToRecipe} />
        )}
        {effectiveView === 'modifiers' && <ModifiersManager clientId={clientId} />}
        {effectiveView === 'ingredients' && (
          <IngredientsManager
            clientId={clientId}
            role={role}
            onNavigate={navigate as (view: string) => void}
            onNavigateToRecipe={navigateToRecipe}
          />
        )}
        {effectiveView === 'recipes' && <RecipesManager clientId={clientId} />}
        {effectiveView === 'users' && <UsersView clientId={clientId} />}
        {effectiveView === 'cashiers' && <CashiersManager />}
        {effectiveView === 'orders' && (
          <OrdersView clientId={clientId} onOpenShift={navigateToShift} />
        )}
        {effectiveView === 'timesheet' && <TimesheetView clientId={clientId} />}
        {effectiveView === 'shifts' && (
          <ShiftsView
            clientId={clientId}
            focusShiftId={focusShiftId}
            onConsumeFocus={() => setFocusShiftId(null)}
            onOpenOrders={navigateToOrders}
            role={role}
          />
        )}
        {effectiveView === 'cash-drops' && <CashDropsView clientId={clientId} />}
        {effectiveView === 'loyalty' && <LoyaltyManager clientId={clientId} />}
        {effectiveView === 'stock-count' && (
          <StockCountView
            clientId={clientId}
            role={role}
            onNavigate={navigate as (view: string) => void}
          />
        )}
        {effectiveView === 'receipt' && <ReceiptTemplateManager clientId={clientId} />}
        {effectiveView === 'customer-display' && (
          <CustomerDisplayManager clientId={clientId} />
        )}
        {effectiveView === 'invoices' && <InvoicesView clientId={clientId} />}
        {effectiveView === 'stock-in' && <StockInView clientId={clientId} />}
        {effectiveView === 'waste' && (
          <WasteLogView
            clientId={clientId}
            onNavigate={navigate as (view: string) => void}
          />
        )}
        {effectiveView === 'prep-batches' && <PrepBatchView clientId={clientId} />}
        {effectiveView === 'pos-diagnostics' && <PosDiagnosticsView clientId={clientId} />}
        {effectiveView === 'suppliers' && <SuppliersManager clientId={clientId} />}
        {effectiveView === 'staff' && <StaffView clientId={clientId} />}
        {effectiveView === 'floor-plan' && tableServiceEnabled && (
          <FloorPlanManager clientId={clientId} />
        )}
        {effectiveView === 'bookings' && bookingsEnabled && <BookingsView clientId={clientId} />}
        {effectiveView === 'booking-insights' && bookingsEnabled && (
          <BookingsInsightsView clientId={clientId} />
        )}
        {effectiveView === 'rooms' && bookingsEnabled && <RoomsManager clientId={clientId} />}
        {effectiveView === 'settings' && <SettingsView clientId={clientId} />}
      </main>
    </div>
  );
}

function NotConfigured() {
  const { t } = useTranslation();
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="brand">Orio</div>
        <h2>{t('auth.notConfiguredTitle')}</h2>
        <p className="muted">{t('auth.notConfiguredBody')}</p>
      </div>
    </div>
  );
}
